/**
 * SplitOnRenderer - Renderer for split-on trigger settings
 *
 * Allows triggers to split array responses into individual workflow instances.
 * Uses a toggle to enable/disable and either a dropdown (when allowed values exist)
 * or a text input (fallback) for the array path expression.
 */

import { makeStyles, tokens, Text, Switch, Input, Field, Combobox, Option } from '@coreai/fluentui-react';
import type { ComboboxProps } from '@coreai/fluentui-react';
import type { SettingRendererProps } from './registry';
import { registerSettingRenderer } from './registry';
import type { SimpleSetting } from '@/types/nodeSettings';

import {
  settingContainerStyle,
  settingDescriptionStyle,
  settingHeaderStyle,
  settingLabelContainerStyle,
  settingLabelStyle,
} from './settingRendererStyles';

const useStyles = makeStyles({
  container: settingContainerStyle,
  header: settingHeaderStyle,
  labelContainer: settingLabelContainerStyle,
  label: settingLabelStyle,
  description: settingDescriptionStyle,
  inputContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  hint: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground4,
    fontStyle: 'italic',
  },
});

export interface SplitOnRendererProps extends SettingRendererProps<SimpleSetting<string>> {
  label?: string;
  description?: string;
  /** Allowed splitOn expressions from the operation manifest */
  allowedValues?: string[] | undefined;
}

/**
 * SplitOnRenderer component for array splitting configuration
 */
export const SplitOnRenderer = ({
  setting,
  onChange,
  readOnly = false,
  label = 'Split On',
  description = 'Split array responses into individual workflow instances',
  allowedValues,
}: SplitOnRendererProps) => {
  const styles = useStyles();

  if (!setting.isSupported) {
    return null;
  }

  const currentValue = setting.value ?? { enabled: false };
  const hasAllowedValues = allowedValues && allowedValues.length > 0;

  const handleToggle = (_: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) => {
    // When enabling with allowed values, auto-select the first option
    const newEnabled = data.checked;
    if (newEnabled && hasAllowedValues && !currentValue.value && allowedValues[0]) {
      onChange({
        enabled: true,
        value: allowedValues[0],
      });
    } else {
      onChange({
        ...currentValue,
        enabled: newEnabled,
      });
    }
  };

  const handleExpressionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    if (newValue) {
      onChange({
        enabled: currentValue.enabled,
        value: newValue,
      });
    } else {
      onChange({
        enabled: currentValue.enabled,
      });
    }
  };

  const handleDropdownChange: ComboboxProps['onOptionSelect'] = (_event, data) => {
    if (data.optionValue) {
      onChange({
        enabled: currentValue.enabled,
        value: data.optionValue,
      });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.labelContainer}>
          <Text className={styles.label}>{label}</Text>
          <Text className={styles.description}>{description}</Text>
        </div>
        <Switch
          checked={currentValue.enabled}
          onChange={handleToggle}
          disabled={readOnly}
          aria-label={`Enable ${label}`}
        />
      </div>

      {currentValue.enabled && (
        <div className={styles.inputContainer}>
          {hasAllowedValues ? (
            <Field label="Array Expression">
              <Combobox
                value={currentValue.value ?? ''}
                autoComplete="off"
                selectedOptions={currentValue.value ? [currentValue.value] : []}
                onOptionSelect={handleDropdownChange}
                disabled={readOnly}
                aria-label="Select split on expression"
                placeholder="Select an array to split on"
                freeform
              >
                {allowedValues.map((option) => (
                  <Option key={option} value={option}>
                    {option}
                  </Option>
                ))}
              </Combobox>
            </Field>
          ) : (
            <Field label="Array Expression">
              <Input
                value={currentValue.value ?? ''}
                onChange={handleExpressionChange}
                placeholder="${triggerBody()?.['value']}"
                disabled={readOnly}
                aria-label="Split on expression"
              />
            </Field>
          )}
          <Text className={styles.hint}>
            {hasAllowedValues
              ? 'Select an array property from the trigger output to split on'
              : 'Expression that returns an array to split on'}
          </Text>
        </div>
      )}
    </div>
  );
};

SplitOnRenderer.displayName = 'SplitOnRenderer';

registerSettingRenderer('splitOn', SplitOnRenderer as React.ComponentType<SettingRendererProps<unknown>>);
